import axios from "axios";
import { ScraperEntry } from "./importScraper";
import { MapLocation } from "./SuperclusterManager";
import RestoCache from "./RestoCache";
import { buildApiUrl } from "./ApiConfig";
import ErrorHandler from "./ErrorHandler";

/**
 * Interface for restaurant report request data
 */
export interface ReportRequest {
  restaurantId: number;
  username: string;
  reportText: string;
  rating: number;
}

/**
 * Interface for restaurant report response data
 */
export interface Report {
  id?: string;
  username: string;
  reportText: string;
  rating: number;
  createdAt: string;
  imageUrl?: string;
}

/**
 * Interface for latest report data from the API
 */
export interface LatestReport {
  id: string;
  username: string;
  reportText: string;
  rating: number;
  createdAt: string;
  restaurantId?: number;
  restaurantName?: string;
  imageUrl?: string;
}

/**
 * Interface for the response of the submit report API call
 */
interface SubmitReportResponse {
  success: boolean;
  data?: unknown; // Use unknown instead of any for better type safety
  error?: string;
}

/**
 * Interface for latitude/longitude coordinates
 */
export interface LatLng {
  lat: number;
  lng: number;
}

export interface LatLngAPI {
  latitude: number;
  longitude: number;
}

/**
 * Interface for city bounds with four corner points
 */
export interface CityBounds {
  topLeft: LatLngAPI;
  topRight: LatLngAPI;
  bottomRight: LatLngAPI;
  bottomLeft: LatLngAPI;
}

/**
 * Interface for city bounds API response
 */
export interface CityBoundsResponse {
  name: string;  // API returns 'name' not 'city'
  bounds: CityBounds;
}

/**
 * Interface for restaurant history item
 */
export interface RestaurantHistoryItem {
  timestamp: string;
  score: number;
  historyItems: string[];
}

/**
 * Interface for restaurant history response
 */
export interface RestaurantHistoryResponse {
  entries: RestaurantHistoryItem[];
}

/**
 * Interface for trend change data
 */
export interface TrendChange {
  restaurantId: number;
  restaurantName: string;
  restaurantAddress: string;
  previousScore: number;
  currentScore: number;
  changeAmount: number;
  changePercentage: number;
  previousDate: string;
  currentDate: string;
}

/**
 * Interface for trends API response
 */
export interface TrendResponse {
  trends: TrendChange[];
  totalCount: number;
}

/**
 * Interface for subscriber count response
 */
export interface SubscriberCountResponse {
  count: number;
}

/**
 * Interfaces for chain API responses
 */
export interface ChainSummary {
  slug: string;
  name: string;
  locationCount: number;
  scoredCount: number;
  scoredPct: number;
  avgScore: number | null;
}

export interface ChainListResponse {
  chains: ChainSummary[];
  totalChains: number;
  minSize: number;
}

export interface ChainLocation {
  id: number;
  name: string;
  address: string;
  postalCode: string | null;
  inspectionForks: number;
  inspectionFrom: number | null;
  latitude: number | null;
  longitude: number | null;
}

export interface ChainDetailResponse extends ChainSummary {
  distribution: {
    score1: number;
    score2: number;
    score3: number;
    score4: number;
    score5: number;
    unscored: number;
  };
  locations: ChainLocation[];
}

export interface RestaurantChainResponse {
  slug: string;
  name: string;
  locationCount: number;
  avgScore: number | null;
}

/**
 * Interfaces for the inspection backlog API
 */
export interface BacklogEntry {
  id: number;
  name: string;
  address: string;
  postalCode: string | null;
  score: number;
  inspectionFrom: number;
  ageYears: number;
  provinceSlug: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface BacklogResponse {
  entries: BacklogEntry[];
  totalCount: number;
  offset: number;
}

export interface BacklogProvinceFacet {
  slug: string;
  nameNl: string;
  nameFr: string;
  name: string;
  count: number;
}

export interface BacklogSummary {
  totalCount: number;
  oldestInspection: number | null;
  score1Count: number;
  score2Count: number;
  byProvince: BacklogProvinceFacet[];
}

/**
 * Interface for score breakdown in comparison response
 */
export interface ScoreBreakdown {
  forksScore: number;
  historyScore: number;
  reportsScore: number;
  forksRaw: number;
  historyItems: string[];
  averageRating: number;
  totalReports: number;
}

/**
 * Interface for city comparison data
 */
export interface CityComparison {
  cityName: string;
  averageScore: number;
  rank: number;
  totalRestaurants: number;
  percentile: number;
}

/**
 * Interface for postal code comparison data
 */
export interface PostalCodeComparison {
  postalCode: string;
  averageScore: number;
  rank: number;
  totalRestaurants: number;
}

/**
 * Interface for category comparison data
 */
export interface CategoryComparison {
  category: string;
  averageScore: number;
  rank: number;
  totalInCategory: number;
}

/**
 * Interface for restaurant comparison response
 */
export interface RestaurantComparisonResponse {
  restaurantId: number;
  restaurantName: string;
  compositeScore: number;
  scoreBreakdown: ScoreBreakdown;
  cityComparison: CityComparison | null;
  postalCodeComparison: PostalCodeComparison | null;
  categoryComparison: CategoryComparison | null;
}

/**
 * Service class for handling restaurant API calls and data processing
 * with integrated caching to reduce redundant API calls
 */
export class RestoApiService {
  // Cache instance to store and retrieve API responses
  private cache: RestoCache;

  /**
   * Initialize the service with a cache instance
   */
  constructor() {
    this.cache = new RestoCache();
  }

  /**
   * Fetches restaurant entries based on map bounds and fork/emoji indices
   * Uses cache when possible to avoid redundant API calls
   *
   * @param bounds The Google Maps bounds to fetch entries within
   * @param forks An array of numbers representing selected fork/emoji indices
   * @returns Promise with processed MapLocation array
   */
  async fetchMapEntries(bounds: google.maps.LatLngBounds, forks: number[]): Promise<MapLocation[]> {
    if (!bounds) return [];

    try {
      // Fetch from API (cache is now primarily for storing results, not preventing calls)
      const boundsJson = bounds.toJSON();
      
      // Build base URL without forks parameter - ALWAYS start with a clean URL
      const baseUrl = buildApiUrl(
        `api/resto?left=${boundsJson.north}&top=${boundsJson.west}&right=${boundsJson.south}&bottom=${boundsJson.east}`
      );
      
      
      
      // Initialize final URL with base URL
      let url = baseUrl;
      
      // ENHANCED CHECK: Only include forks parameter if forks array is not empty and contains valid values
      // Strict check to ensure forks is a non-empty array with valid numbers
      if (forks &&
          Array.isArray(forks) &&
          forks.length > 0 &&
          forks.every(score => typeof score === 'number' && !isNaN(score))) {
        const forksString = forks.join(',');
        url = `${baseUrl}&forks=${forksString}`;
        
      } 
      
      let entries: ScraperEntry[] = [];

      try {
        
        const response = await axios.get(url, {
          timeout: 10000,
          headers: {
            "X-CSRF-Token": "dummy-token",
          },
        });
        

        if (Array.isArray(response.data)) {
          entries = response.data.map((item) => {
            return {
              id: String(item.id || ""),
              name: item.name || "",
              address: item.address || "",
              operatorNumber: item.operatorNumber || "",
              operatorType: item.operatorType || "",
              operatorStatus: item.operatorStatus || "",
              date: item.date || "",
              image: item.image || "",
              tooltip: item.tooltip || "",
              links: item.links || "",
              inspectionFrom: item.inspectionFrom !== undefined ? Number(item.inspectionFrom) : 0,
              inspectionForks: item.inspectionForks ? String(item.inspectionForks) : "",
              inspectionSmiley: item.inspectionSmiley || "",
              inspectionDescription: item.inspectionDescription || "",
              inspectionAbbreviationNl: item.inspectionAbbreviationNl || "",
              inspectionAbbreviationFr: item.inspectionAbbreviationFr || "",
              inspectionCode: item.inspectionCode || "",
              inspectionExitCode: item.inspectionExitCode || "",
              inspectionExitCodeDescriptionNl: item.inspectionExitCodeDescNl || "",
              inspectionExitCodeDescriptionFr: item.inspectionExitCodeDescFr || "",
              latitude: item.latitude,
              longitude: item.longitude,
              // Ensure historyItems is properly initialized as an array
              historyItems: Array.isArray(item.historyItems) ? item.historyItems :
                           (item.historyItems ? [item.historyItems] : []),
            };
          });
        } else if (typeof response.data === "object") {
          for (const key in response.data) {
            if (Array.isArray(response.data[key])) {
              
              entries = response.data[key];
              break;
            }
          }
        }
        
        
      } catch (error) {
        console.error("Error fetching from API:", error);
      }

      const mappedLocations = entries.map((entry) => ({
        lat: entry.latitude!,
        lng: entry.longitude!,
        entry: entry,
        id: entry.id,
      }));
      
      

      // Add the newly fetched results to the central cache
      if (mappedLocations.length > 0) {
        this.cache.addToCache(mappedLocations);
      }

      // Now, retrieve all locations within the requested bounds from the cache
      // This includes potentially newly added ones and previously cached ones.
      // Pass the forks array directly to the cache
      
      const locationsFromCache = this.cache.getLocationsInBounds(bounds, forks);
      
      
      // After fix, verify that no locations are returned when forks is an empty array
      if (forks && forks.length === 0 && locationsFromCache.length > 0) {
        console.error("[RestoApiService] ERROR: Retrieved locations despite empty forks array!");
      }
      
      return locationsFromCache;
    } catch (error) {
      console.error("Error fetching map entries:", error);
      return [];
    }
  }

  /**
   * Synchronously retrieves locations from cache without making an API call.
   * Used for instant cache-first rendering on map drag.
   *
   * @param bounds The Google Maps bounds to query within
   * @param forks An array of numbers representing selected fork/emoji indices
   * @returns Array of cached MapLocation objects within bounds
   */
  getCachedLocationsInBounds(bounds: google.maps.LatLngBounds, forks: number[]): MapLocation[] {
    return this.cache.getLocationsInBounds(bounds, forks);
  }

  /**
   * Clear the cache
   * Useful when forcing a refresh of data
   */
  clearCache(): void {
    this.cache.clearCache();
  }

  /**
   * Get the total number of entries in the cache
   */
  getCacheEntryCount(): number {
    return this.cache.getTotalEntries();
  }

  // getCacheSize method removed as it's no longer supported by the refactored RestoCache

  /**
   * Submits a new restaurant report to the backend API
   *
   * @param reportData The report data to submit
   * @param imageFile Optional image file to upload with the report
   * @returns Promise with the response data or error
   */
  async submitRestaurantReport(reportData: ReportRequest, imageFile?: File): Promise<SubmitReportResponse> {
    try {
      // Always use FormData since the server expects multipart
      const formData = new FormData();
      
      // Add individual form fields as expected by the server
      formData.append('restaurantId', reportData.restaurantId.toString());
      formData.append('username', reportData.username);
      formData.append('reportText', reportData.reportText);
      formData.append('rating', reportData.rating.toString());
      
      // Add image file if provided
      if (imageFile) {
        formData.append('image', imageFile);
      } else {
      }
      
      const response = await axios.post(buildApiUrl("api/reports/submit"), formData, {
        headers: {
          // Content-Type will be set automatically by axios for FormData
          // No user token needed for anonymous reporting
        },
        timeout: imageFile ? 30000 : 10000, // Longer timeout if uploading image
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      console.error("Error submitting restaurant report:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Fetches reports for a specific restaurant
   *
   * @param restaurantId The ID of the restaurant to fetch reports for
   * @returns Promise with array of reports or error
   */
  async fetchRestaurantReports(restaurantId: number): Promise<{ success: boolean; data?: Report[]; error?: string }> {
    
    
    try {
      const url = buildApiUrl(`api/reports/${restaurantId}`);
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });
      
      

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      console.error(`Error fetching reports for restaurant ID ${restaurantId}:`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Confirms a report (thumbs-up validation)
   */
  async confirmReport(reportId: number): Promise<{ success: boolean; confirmations?: number; error?: string }> {
    try {
      const url = buildApiUrl(`api/reports/${reportId}/confirm`);
      const response = await axios.post(url, null, {
        headers: { "X-CSRF-Token": "dummy-token" },
        timeout: 5000,
      });
      return { success: true, confirmations: response.data?.confirmations };
    } catch (error: any) {
      if (error?.response?.status === 409) {
        return { success: false, error: "already_confirmed" };
      }
      return { success: false, error: error instanceof Error ? error.message : "Failed to confirm" };
    }
  }

  /**
   * Fetches the latest reports from all restaurants
   *
   * @returns Promise with array of latest reports or error
   */
  async fetchLatestReports(): Promise<{ success: boolean; data?: LatestReport[]; error?: string }> {
    try {
      const url = buildApiUrl("api/reports/latest");
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300) {
        // Add IDs to reports if they don't have them
        const reportsWithIds = response.data.map((report: any, index: number) => ({
          ...report,
          id: report.id || `report-${Date.now()}-${index}`,
        }));
        
        return {
          success: true,
          data: reportsWithIds,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchLatestReports');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Fetches details for a specific restaurant by its ID
   *
   * @param restoId The ID of the restaurant to fetch
   * @returns Promise with the restaurant data (ScraperEntry) or error
   */
  async fetchRestoById(restoId: string): Promise<{ success: boolean; data?: ScraperEntry; error?: string }> {
        
    
    try {
      const url = buildApiUrl(`api/resto/${restoId}`);
      
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });
      
      
      // More detailed response logging
      if (response.data === null || response.data === undefined) {
        
      } else if (typeof response.data === 'object' && Object.keys(response.data).length === 0) {
        
      }

      // Check if the response is successful and data exists
      if (response.status >= 200 && response.status < 300 && response.data) {
        // Map the response data to ScraperEntry format
        const entry: ScraperEntry = {
          id: String(response.data.id || ""),
          name: response.data.name || "",
          address: response.data.address || "",
          operatorNumber: response.data.operatorNumber || "",
          operatorType: response.data.operatorType || "",
          operatorStatus: response.data.operatorStatus || "",
          date: response.data.date || "",
          image: response.data.image || "",
          tooltip: response.data.tooltip || "",
          links: response.data.links || "",
          inspectionFrom: response.data.inspectionFrom !== undefined ? Number(response.data.inspectionFrom) : 0,
          inspectionForks: response.data.inspectionForks ? String(response.data.inspectionForks) : "",
          inspectionSmiley: response.data.inspectionSmiley || "",
          inspectionDescription: response.data.inspectionDescription || "",
          inspectionAbbreviationNl: response.data.inspectionAbbreviationNl || "",
          inspectionAbbreviationFr: response.data.inspectionAbbreviationFr || "",
          inspectionCode: response.data.inspectionCode || "",
          inspectionExitCode: response.data.inspectionExitCode || "",
          inspectionExitCodeDescriptionNl: response.data.inspectionExitCodeDescNl || "",
          inspectionExitCodeDescriptionFr: response.data.inspectionExitCodeDescFr || "",
          latitude: response.data.latitude,
          longitude: response.data.longitude,
          // Ensure historyItems is properly initialized as an array
          historyItems: Array.isArray(response.data.historyItems) ? response.data.historyItems :
                        (response.data.historyItems ? [response.data.historyItems] : []),
        };
        
        
        return {
          success: true,
          data: entry,
        };
      } else {
        
        throw new Error(`Request failed with status code ${response.status} or no data received`);
      }
    } catch (error) {
      console.error(`Error fetching restaurant with ID ${restoId}:`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Fetches restaurant data with proper parameter handling
   * This method ensures the forks parameter is only included when valid
   *
   * @param params The parameters for the API call
   * @returns Promise with the response data
   */
  async fetchRestos(params: Record<string, any>): Promise<any> {
    try {
      
      
      // Extract endpoint from params or use default
      const endpoint = params.endpoint || 'resto';
      delete params.endpoint; // Remove from params to avoid sending as query param
      
      // Create a new URLSearchParams object for building the query string
      const finalSearchParams = new URLSearchParams();
      
      // First add all non-forks parameters to the search params
      for (const [key, value] of Object.entries(params)) {
        if (key !== 'forks' && value !== undefined && value !== null) {
          finalSearchParams.append(key, String(value));
        }
      }
      
      // Special handling for forks parameter - ONLY add if it meets ALL criteria
      if (params &&
          typeof params.forks === 'string' &&
          params.forks.trim().length > 0 && // Added trim() to ensure no whitespace-only strings
          params.forks.split(',').every(v => !isNaN(Number(v)))) {
        finalSearchParams.append('forks', params.forks);
        
      }
      
      // Build the final URL with the appropriate endpoint
      const url = buildApiUrl(`api/resto/${endpoint}?${finalSearchParams.toString()}`);
      
      
      // Final verification - ensure URL doesn't contain forks=2 when it shouldn't
      if (url.includes('forks=2') && (!params.forks || params.forks === '')) {
        console.error('[RestoApiService] fetchRestos - CRITICAL ERROR: URL contains forks=2 when it should not!');
        // Create a new URL without the forks parameter
        const urlObj = new URL(url);
        urlObj.searchParams.delete('forks');
        const correctedUrl = urlObj.toString();
        
        // Use the corrected URL instead
        return axios.get(correctedUrl, {
          headers: {
            "X-CSRF-Token": "dummy-token",
          },
          timeout: 10000,
        }).then(response => response.data);
      }
      
      // Make the API call
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });
      
      return response.data;
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      throw error;
    }
  }

  /**
   * Fetches city bounds data for a given city name
   *
   * @param cityName The name of the city to fetch bounds for
   * @returns Promise with city bounds data or error
   */
  async fetchCityBounds(cityName: string): Promise<{ success: boolean; data?: CityBoundsResponse; error?: string }> {
    try {
      const url = buildApiUrl(`api/city/bounds?name=${encodeURIComponent(cityName)}`);
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchCityBounds');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Fetches history data for a specific restaurant
   *
   * @param restaurantId The ID of the restaurant to fetch history for
   * @returns Promise with restaurant history data or error
   */
  async fetchRestaurantHistory(restaurantId: number): Promise<{ success: boolean; data?: RestaurantHistoryResponse; error?: string }> {
    try {
      const url = buildApiUrl(`api/resto/${restaurantId}/history`);
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchRestaurantHistory');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Fetches subscriber count for a specific restaurant
   *
   * @param restaurantId The ID of the restaurant to fetch subscriber count for
   * @returns Promise with subscriber count data or error
   */
  async fetchSubscriberCount(restaurantId: number): Promise<{ success: boolean; data?: SubscriberCountResponse; error?: string }> {
    try {
      const url = buildApiUrl(`api/resto/${restaurantId}/subscriber-count`);
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchSubscriberCount');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Fetches comparison data for a specific restaurant
   *
   * @param restaurantId The ID of the restaurant to fetch comparison for
   * @returns Promise with restaurant comparison data or error
   */
  async fetchRestaurantComparison(restaurantId: number): Promise<{ success: boolean; data?: RestaurantComparisonResponse; error?: string }> {
    try {
      const url = buildApiUrl(`api/resto/${restaurantId}/comparison`);

      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchRestaurantComparison');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Fetches upward trending restaurants data
   *
   * @param limit Number of results to return (default: 20)
   * @param citySlug Optional city to filter trends to (server-side)
   * @returns Promise with upward trends data or error
   */
  async fetchUpwardTrends(limit: number = 20, citySlug?: string): Promise<{ success: boolean; data?: TrendResponse; error?: string }> {
    try {
      const cityParam = citySlug ? `&city=${encodeURIComponent(citySlug)}` : '';
      const url = buildApiUrl(`api/trends/upward?limit=${limit}${cityParam}`);
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchUpwardTrends');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Fetches downward trending restaurants data
   *
   * @param limit Number of results to return (default: 20)
   * @param citySlug Optional city to filter trends to (server-side)
   * @returns Promise with downward trends data or error
   */
  async fetchDownwardTrends(limit: number = 20, citySlug?: string): Promise<{ success: boolean; data?: TrendResponse; error?: string }> {
    try {
      const cityParam = citySlug ? `&city=${encodeURIComponent(citySlug)}` : '';
      const url = buildApiUrl(`api/trends/downward?limit=${limit}${cityParam}`);
      
      const response = await axios.get(url, {
        headers: {
          "X-CSRF-Token": "dummy-token",
        },
        timeout: 10000,
      });

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300 && response.data) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        throw new Error(`Request failed with status code ${response.status}`);
      }
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchDownwardTrends');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Fetches the chain rankings list
   */
  async fetchChains(options: { sort?: 'size' | 'score'; order?: 'asc' | 'desc'; minSize?: number; limit?: number } = {}): Promise<{ success: boolean; data?: ChainListResponse; error?: string }> {
    try {
      const { sort = 'size', order = 'desc', minSize = 5, limit = 200 } = options;
      const url = buildApiUrl(`api/chains?sort=${sort}&order=${order}&minSize=${minSize}&limit=${limit}`);
      const response = await axios.get(url, { timeout: 10000 });
      if (response.status >= 200 && response.status < 300 && response.data) {
        return { success: true, data: response.data };
      }
      throw new Error(`Request failed with status code ${response.status}`);
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchChains');
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Fetches chain detail (aggregates + all locations) by slug
   */
  async fetchChainDetail(slug: string): Promise<{ success: boolean; data?: ChainDetailResponse; error?: string }> {
    try {
      const url = buildApiUrl(`api/chains/${encodeURIComponent(slug)}`);
      const response = await axios.get(url, { timeout: 10000 });
      if (response.status >= 200 && response.status < 300 && response.data) {
        return { success: true, data: response.data };
      }
      throw new Error(`Request failed with status code ${response.status}`);
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchChainDetail');
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Fetches the inspection backlog (stale failing establishments)
   */
  async fetchBacklog(options: { province?: string; limit?: number; offset?: number } = {}): Promise<{ success: boolean; data?: BacklogResponse; error?: string }> {
    try {
      const { province, limit = 100, offset = 0 } = options;
      const provinceParam = province ? `&province=${encodeURIComponent(province)}` : '';
      const url = buildApiUrl(`api/backlog?limit=${limit}&offset=${offset}${provinceParam}`);
      const response = await axios.get(url, { timeout: 10000 });
      if (response.status >= 200 && response.status < 300 && response.data) {
        return { success: true, data: response.data };
      }
      throw new Error(`Request failed with status code ${response.status}`);
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchBacklog');
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Fetches the backlog summary (counts + province facets)
   */
  async fetchBacklogSummary(): Promise<{ success: boolean; data?: BacklogSummary; error?: string }> {
    try {
      const url = buildApiUrl('api/backlog/summary');
      const response = await axios.get(url, { timeout: 10000 });
      if (response.status >= 200 && response.status < 300 && response.data) {
        return { success: true, data: response.data };
      }
      throw new Error(`Request failed with status code ${response.status}`);
    } catch (error) {
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchBacklogSummary');
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Fetches the chain a restaurant belongs to; 404 means "not in a chain"
   */
  async fetchRestaurantChain(restaurantId: number): Promise<{ success: boolean; data?: RestaurantChainResponse; notInChain?: boolean; error?: string }> {
    try {
      const url = buildApiUrl(`api/resto/${restaurantId}/chain`);
      const response = await axios.get(url, { timeout: 8000 });
      if (response.status >= 200 && response.status < 300 && response.data) {
        return { success: true, data: response.data };
      }
      throw new Error(`Request failed with status code ${response.status}`);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return { success: true, notInChain: true };
      }
      const errorMessage = ErrorHandler.formatErrorMessage(error);
      ErrorHandler.logError(error, 'RestoApiService.fetchRestaurantChain');
      return { success: false, error: errorMessage };
    }
  }
}

export default RestoApiService;
